/**
 * 채팅 "URL만 있는 메시지" 중 **알려진 미디어 프로바이더**(YouTube/Vimeo/Spotify/SoundCloud)를
 * 인라인 임베드 플레이어로 변환하기 위한 순수 util. (chat-inline-media-playback — 2026-07-12)
 *
 * 정책:
 *   - extractSoleUrl 을 통과한 단일 http(s) URL 만 대상(기존 링크 미리보기와 동일 진입 조건).
 *   - 화이트리스트 프로바이더 + 엄격한 id 패턴 매칭만 임베드 → 임의 오리진 iframe 주입 차단.
 *   - 매칭 실패 → null (호출측 LinkPreviewCard 가 기존 도메인/OG 카드로 폴백, 무회귀).
 *   - 개인정보: YouTube 는 youtube-nocookie 임베드 도메인 사용.
 *
 * additive — 기존 link-preview 경로 무변경. 미매칭 URL 영향 0.
 */

export type MediaEmbedProvider = 'youtube' | 'vimeo' | 'spotify' | 'soundcloud';

export interface MediaEmbed {
  provider: MediaEmbedProvider;
  /** iframe src (임베드 도메인). */
  embedUrl: string;
  /** video = 16:9 프레임, audio = 고정 높이 컴팩트 플레이어. */
  kind: 'video' | 'audio';
}

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const SPOTIFY_ID = /^[A-Za-z0-9]{22}$/;
const SPOTIFY_TYPES = new Set(['track', 'album', 'playlist', 'episode', 'show', 'artist']);

function stripWww(host: string): string {
  return host.replace(/^www\./, '').replace(/^m\./, '');
}

function youtubeEmbed(u: URL): MediaEmbed | null {
  const host = stripWww(u.hostname.toLowerCase());
  let id: string | null = null;
  if (host === 'youtu.be') {
    id = u.pathname.slice(1).split('/')[0] || null;
  } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (u.pathname === '/watch') {
      id = u.searchParams.get('v');
    } else {
      const m = u.pathname.match(/^\/(?:embed|shorts|v|live)\/([^/?#]+)/);
      id = m ? m[1]! : null;
    }
  }
  if (!id || !YT_ID.test(id)) return null;
  return { provider: 'youtube', kind: 'video', embedUrl: `https://www.youtube-nocookie.com/embed/${id}` };
}

function vimeoEmbed(u: URL): MediaEmbed | null {
  const host = stripWww(u.hostname.toLowerCase());
  if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;
  const m = u.pathname.match(/\/(\d{6,})/);
  if (!m) return null;
  return { provider: 'vimeo', kind: 'video', embedUrl: `https://player.vimeo.com/video/${m[1]}` };
}

function spotifyEmbed(u: URL): MediaEmbed | null {
  const host = stripWww(u.hostname.toLowerCase());
  if (host !== 'open.spotify.com') return null;
  // /{type}/{id} 또는 /embed/{type}/{id}
  const parts = u.pathname.replace(/^\/embed/, '').split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const [type, id] = parts;
  if (!SPOTIFY_TYPES.has(type!) || !SPOTIFY_ID.test(id!)) return null;
  return { provider: 'spotify', kind: 'audio', embedUrl: `https://open.spotify.com/embed/${type}/${id}` };
}

function soundcloudEmbed(u: URL): MediaEmbed | null {
  const host = stripWww(u.hostname.toLowerCase());
  if (host !== 'soundcloud.com') return null;
  // /{user}/{track} 형태만(임의 경로가 아닌 최소 2세그먼트). 위젯이 url 파라미터로 재해석.
  const parts = u.pathname.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const encoded = encodeURIComponent(`https://soundcloud.com${u.pathname}`);
  return {
    provider: 'soundcloud',
    kind: 'audio',
    embedUrl: `https://w.soundcloud.com/player/?url=${encoded}&color=%23ff5500&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`,
  };
}

/**
 * 단일 http(s) URL → 미디어 임베드 정보, 아니면 null.
 * http/https 스킴만 허용(javascript:/data: 등 차단).
 */
export function detectMediaEmbed(url: string | null | undefined): MediaEmbed | null {
  if (!url) return null;
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  return (
    youtubeEmbed(u) ?? vimeoEmbed(u) ?? spotifyEmbed(u) ?? soundcloudEmbed(u) ?? null
  );
}
